<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue';

import { KaCheckbox } from '@/kasten-components/components/KaCheckbox';
import { KaSkeletonCheckbox } from '@/kasten-components/components/KaSkeleton';
import type { VueClass } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';

import KaTableHeadCell from './KaTableHeadCell.vue';
import { injectTableContext, SELECT_COLUMN_NAME } from './model';

const props = withDefaults(
  defineProps<{
    selectAll?: boolean;
    modelValue?: boolean;
    class?: VueClass;
  }>(),
  {
    selectAll: false,
    modelValue: undefined,
    class: '',
  },
);

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
  (e: 'update:modelValue', value: boolean): void;
}>();

const {
  selectedAllState,
  selectedSomeState,
  isTableEmpty,
  isLoading,
  setIsMultiSelectable,
  onToggleAll,
} = injectTableContext();

const computedValue = computed<boolean>({
  get: () => {
    if (selectedSomeState.value) return false;
    return selectedAllState.value;
  },
  set: (value) => {
    onToggleAll(value);
    emit('update:modelValue', value);
  },
});

const selectColumnAriaLabel = computed(() => {
  if (selectedAllState.value) {
    return 'Deselect all rows';
  }

  return 'Select all rows';
});

const handleClick = (e: MouseEvent) => {
  if (isTableEmpty.value) return;

  selectedAllState.value ? (computedValue.value = false) : (computedValue.value = true);

  emit('click', e);
};

watch(
  () => props.selectAll,
  (value) => setIsMultiSelectable(value),
  { immediate: true },
);

onBeforeUnmount(() => setIsMultiSelectable(false));
</script>

<template>
  <KaTableHeadCell
    v-if="props.selectAll"
    :name="SELECT_COLUMN_NAME"
    class="checkboxCell w-[1px]"
    :class="
      cnMerge(
        {
          'cursor-pointer': !isTableEmpty && !isLoading,
        },
        '[&&&>div>div]:h-4',
        '[&&&>div>div>input]:mt-0 [&&&>div>div>input]:cursor-pointer [&&&>div>div>input]:checked:bg-[currentColor]',
        props.class,
      )
    "
    data-testid="selectAllCell"
    @click.stop="handleClick"
  >
    <KaSkeletonCheckbox v-if="isLoading" decorative />
    <KaCheckbox
      v-else
      :id="`select-all`"
      v-model="computedValue"
      :indeterminate="selectedSomeState && !selectedAllState"
      class="ml-[1px]"
      :class="{
        '[&&&]:cursor-not-allowed': isTableEmpty,
      }"
      hide-label
      :label="selectColumnAriaLabel"
      name="select-all"
      :disabled="isTableEmpty"
      :aria-disabled="isTableEmpty"
      @click.stop
    />
  </KaTableHeadCell>
  <td v-else class="w-0" />
</template>
